// IT Backlog Types

export type BacklogStatus = 'backlog' | 'planned' | 'in_progress' | 'done';
export type BacklogPriority = 'low' | 'medium' | 'high' | 'urgent' | 'critical';
export type BacklogCategory = 'bug_fix' | 'feature' | 'improvement' | 'infrastructure' | 'support' | 'task';

export interface ItBacklogItem {
  id: string;
  title: string;
  description: string | null;
  acceptance_criteria: string | null;
  status: BacklogStatus;
  priority: BacklogPriority;
  planned_week: string | null;
  estimated_hours: number | null;
  actual_hours: number | null;
  category: BacklogCategory;
  module: string | null;
  requested_by: string | null;
  assigned_to: string | null;
  related_bug_report_id: string | null;
  due_date: string | null;
  started_at: string | null;
  completed_at: string | null;
  notes: string | null;
  blockers: string | null;
  created_at: string;
  updated_at: string;
  
  // Joined data
  requester?: {
    id: string;
    display_name: string | null;
    first_name: string | null;
    last_name: string | null;
  } | null;
  assignee?: {
    id: string;
    display_name: string | null;
    first_name: string | null;
    last_name: string | null;
  } | null;
  bug_report?: {
    id: string;
    title: string;
  } | null;
}

export interface CreateBacklogItemData {
  title: string;
  description?: string;
  acceptance_criteria?: string;
  priority?: BacklogPriority;
  category?: BacklogCategory;
  module?: string;
  estimated_hours?: number;
  due_date?: string;
  related_bug_report_id?: string;
  notes?: string;
  requested_by?: string;
  assigned_to?: string;
}

export interface UpdateBacklogItemData extends Partial<CreateBacklogItemData> {
  status?: BacklogStatus;
  planned_week?: string | null;
  actual_hours?: number;
  started_at?: string;
  completed_at?: string;
  blockers?: string;
}

export interface BacklogStats {
  total: number;
  thisWeek: number;
  critical: number;
  plannedHours: number;
  completedThisWeek: number;
}

// Status config for UI
export const STATUS_CONFIG: Record<BacklogStatus, { label: string; color: string }> = {
  backlog: { label: 'Backlog', color: 'bg-muted text-muted-foreground' },
  planned: { label: 'Planejado', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  in_progress: { label: 'Fazendo', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
  done: { label: 'Concluído', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
};

export const PRIORITY_CONFIG: Record<BacklogPriority, { label: string; color: string; icon: string }> = {
  low: { label: 'Baixa', color: 'text-slate-500', icon: '⬇️' },
  medium: { label: 'Média', color: 'text-blue-500', icon: '➡️' },
  high: { label: 'Alta', color: 'text-orange-500', icon: '⬆️' },
  urgent: { label: 'Urgente', color: 'text-red-500', icon: '🔺' },
  critical: { label: 'Crítico', color: 'text-red-600', icon: '🔴' },
};

export const CATEGORY_CONFIG: Record<BacklogCategory, { label: string; icon: string }> = {
  bug_fix: { label: 'Bug Fix', icon: '🐛' },
  feature: { label: 'Feature', icon: '✨' },
  improvement: { label: 'Melhoria', icon: '📈' },
  infrastructure: { label: 'Infra', icon: '🏗️' },
  support: { label: 'Suporte', icon: '🎧' },
  task: { label: 'Tarefa', icon: '📋' },
};

// System modules for categorization
export const SYSTEM_MODULES = [
  'Deals',
  'Leads',
  'Contacts',
  'Cases',
  'Finance',
  'WhatsApp',
  'Tasks',
  'Calls',
  'Reports',
  'Settings',
  'Admin',
  'Onboarding',
  'Knowledge Base',
  'Auth',
  'Infra/DevOps',
  'Outro',
] as const;
